import { useCallback, useEffect, useRef, useState } from 'react'
import { listMobileReleases, type MobileReleaseList } from '../../../services/ownerDemoLaneService'

export type MobileReleasesState = {
  data: MobileReleaseList | null
  loading: boolean
  error: string | null
  reload: () => Promise<void>
}

type Outcome = { ok: true; value: MobileReleaseList } | { ok: false; message: string }

const messageOf = (error: unknown): string =>
  error instanceof Error ? error.message : typeof error === 'string' ? error : 'Unknown error'

const fetchReleases = (): Promise<Outcome> =>
  listMobileReleases().then(
    (value): Outcome => ({ ok: true, value }),
    (error: unknown): Outcome => ({ ok: false, message: messageOf(error) }),
  )

/** Loads the phone app releases and channel pointers once (when enabled) and again on `reload()`. */
export function useMobileReleases(enabled: boolean): MobileReleasesState {
  const [data, setData] = useState<MobileReleaseList | null>(null)
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState<string | null>(null)
  const mounted = useRef(true)

  const apply = useCallback((outcome: Outcome) => {
    if (!mounted.current) return
    if (outcome.ok) {
      setData(outcome.value)
      setError(null)
    } else {
      setError(outcome.message)
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    mounted.current = true
    if (enabled) void fetchReleases().then(apply)
    return () => { mounted.current = false }
  }, [enabled, apply])

  const reload = useCallback(async () => {
    setLoading(true)
    apply(await fetchReleases())
  }, [apply])

  return { data, loading, error, reload }
}
